/**
 * معالج تسجيل المصورين الجديد — Stepper / Wizard
 * مسار: /photographer-register
 * لا يوجد رابط لهذه الصفحة في أي زر حالياً — يُضاف لاحقاً عند الجاهزية
 *
 * الخطوات:
 *   1. البيانات الأساسية  (الاسم، الجوال، الإيميل، المدينة)
 *   2. التخصصات والتصوير (تخصصات، وسائل تصوير + جهاز، تعديل، معدات)
 *   3. التحقق من الجوال   (OTP → إنشاء الحساب + حفظ كل البيانات النصية)
 *   4. الصورة + المعرض   (رفع صورة شخصية + 3 صور معرض على الأقل)
 *   5. نطاق الخدمة        (خريطة تحديد منطقة العمل)
 *   6. توثيق الهوية       (اختياري — رفع الهوية للمراجعة الإدارية)
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { sendOTP as sendOTPApi, normalizePhone } from '../../sendOtp';
import { verifyOtpAndSignIn } from '../../services/verifyOtpAndSignIn';
import AppIcon from '../../components/AppIcon';
import MashhadLogo from '../../components/MashhadLogo';
import LocationPickerMap from '../../components/LocationPickerMap';
import Toast from '../../components/ui/Toast';
import OtpInput from '../../components/OtpInput';
import { extractCityDistrict } from '../../utils/addressParsing';
import { calculateDistance } from '../../services/locationService';
import { getCurrentPosition, isGeolocationSupported } from '../../utils/location';
import {
  backfillDronePermitFromStorage,
  getDroneExpiryBadgeClass,
  getDroneExpiryStatus,
  getDroneExpiryStatusLabel,
  getDronePermitStatus,
  getDronePermitStatusLabel,
  photographerUsesDrone,
  syncShootingMethodsToProfile,
  uploadDronePermit,
  validateDronePermitFile,
} from '../../services/dronePermitService';
import {
  backfillFreelancePermitFromStorage,
  getFreelancePermitStatus,
  getFreelancePermitStatusLabel,
  photographerIsIndividual,
  uploadFreelancePermit,
  validateFreelancePermitFile,
} from '../../services/freelancePermitService';

// ─── ثوابت البيانات ────────────────────────────────────────────────────────────
const SPECIALIZATIONS = [
  { value: 'event',       label: 'تصوير المناسبات' },
  { value: 'product',     label: 'تصوير المنتجات' },
  { value: 'portrait',    label: 'التصوير الشخصي' },
  { value: 'food',        label: 'تصوير الطعام' },
  { value: 'real-estate', label: 'تصوير العقارات' },
  { value: 'wedding',     label: 'حفلات الزفاف' },
];

const CITIES = [
  'الرياض','جدة','الدمام','الخبر','المدينة المنورة','مكة المكرمة',
  'الطائف','أبها','خميس مشيط','جازان','نجران','الباحة','تبوك','حائل','بريدة','سكاكا',
];

const SHOOTING_METHODS = [
  { value: 'professional_camera', label: 'كاميرا احترافية', icon: 'Camera',    placeholder: 'مثال: Canon EOS R5, Sony A7 IV' },
  { value: 'mobile_camera',       label: 'كاميرا جوال',     icon: 'Smartphone', placeholder: 'مثال: iPhone 15 Pro, Samsung S24' },
  { value: 'drone',               label: 'تصوير جوي (درون)', icon: 'Radio',    placeholder: 'مثال: DJI Mini 4 Pro, Autel Evo' },
];

const EQUIPMENT_OPTIONS = [
  { value: 'basic-editing',        label: 'تعديل أساسي (تصحيح إضاءة وألوان)' },
  { value: 'professional-editing', label: 'تعديل احترافي متقدم' },
];

const AVAILABLE_EQUIPMENT = [
  { value: 'عدسة احترافية', label: 'عدسة احترافية' },
  { value: 'مانع اهتزاز',   label: 'مانع اهتزاز' },
  { value: 'إضاءة استديو',  label: 'إضاءة استديو' },
  { value: 'خلفيات تصوير',  label: 'خلفيات تصوير' },
  { value: 'مايكروفون',     label: 'مايكروفون' },
];

const CITY_COORDS = {
  'الرياض': { lat: 24.7136, lng: 46.6753 },
  'جدة':    { lat: 21.4858, lng: 39.1925 },
  'الدمام': { lat: 26.4207, lng: 50.0888 },
  'الخبر':  { lat: 26.2172, lng: 50.1971 },
  'المدينة المنورة': { lat: 24.5247, lng: 39.5692 },
  'مكة المكرمة':     { lat: 21.3891, lng: 39.8579 },
  'الطائف':  { lat: 21.2703, lng: 40.4158 },
  'أبها':    { lat: 18.2164, lng: 42.5042 },
  'خميس مشيط': { lat: 18.3000, lng: 42.7333 },
  'جازان':   { lat: 16.8894, lng: 42.5706 },
  'نجران':   { lat: 17.4924, lng: 44.1277 },
  'الباحة':  { lat: 20.0129, lng: 41.4677 },
  'تبوك':    { lat: 28.3998, lng: 36.5700 },
  'حائل':    { lat: 27.5114, lng: 41.7208 },
  'بريدة':   { lat: 26.3260, lng: 43.9750 },
  'سكاكا':   { lat: 29.9697, lng: 40.2064 },
};

const WIZARD_DATA_KEY  = 'mashhad_wizard_data';
const WIZARD_STEP_KEY  = 'mashhad_wizard_step';
const WIZARD_USER_KEY  = 'mashhad_wizard_user';
const WIZARD_NOTIF_KEY = 'mashhad_wizard_notif_sent';
const TOTAL_STEPS = 6;

// ─── المكوّن الرئيسي ──────────────────────────────────────────────────────────
const PhotographerRegistrationWizard = () => {
  const navigate = useNavigate();

  // ── استعادة التقدم المحفوظ ────────────────────────────────────────────────
  const savedStep = (() => {
    try { return parseInt(localStorage.getItem(WIZARD_STEP_KEY) || '1', 10); } catch { return 1; }
  })();
  const savedData = (() => {
    try { return JSON.parse(localStorage.getItem(WIZARD_DATA_KEY) || 'null'); } catch { return null; }
  })();
  const savedUserId = (() => {
    try { return localStorage.getItem(WIZARD_USER_KEY) || null; } catch { return null; }
  })();

  const [currentStep, setCurrentStep] = useState(savedStep >= 1 && savedStep <= TOTAL_STEPS ? savedStep : 1);
  const [userId, setUserId]   = useState(savedUserId);
  const [toast, setToast]     = useState(null);
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);
  const [processingMsg, setProcessingMsg] = useState('');

  // ── بيانات النموذج (خطوات 1-2) ──────────────────────────────────────────
  const [formData, setFormData] = useState({
    fullName:            savedData?.fullName            || '',
    email:               savedData?.email               || '',
    phone:               savedData?.phone               || '',
    city:                savedData?.city                || '',
    entityType:          savedData?.entityType          || '',
    businessName:        savedData?.businessName        || '',
    specializations:     savedData?.specializations     || [],
    shootingMethods:     savedData?.shootingMethods     || [],
    shootingMethodModels:savedData?.shootingMethodModels|| {},
    equipment:           savedData?.equipment           || [],
    languages:           savedData?.languages           || [],
    agreeToTerms:        false,
  });

  // ── OTP ───────────────────────────────────────────────────────────────────
  const [otpCode,   setOtpCode]   = useState('');
  const [otpSent,   setOtpSent]   = useState(false);
  const [msegatId,  setMsegatId]  = useState(null);
  const [countdown, setCountdown] = useState(0);
  const verifyInFlightRef = useRef(false);

  // ── خطوة 4: الصورة والمعرض ───────────────────────────────────────────────
  const [avatarFile,    setAvatarFile]    = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [portfolioItems, setPortfolioItems] = useState([]); // [{file, preview, uploading}]
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const avatarInputRef     = useRef(null);
  const portfolioInputRef  = useRef(null);

  // ── خطوة 5: نطاق الخدمة ──────────────────────────────────────────────────
  const [serviceCenter,  setServiceCenter]  = useState({ lat: null, lng: null });
  const [serviceRadius,  setServiceRadius]  = useState(20);
  const [serviceLabel,   setServiceLabel]   = useState('');
  const [savingService,    setSavingService]    = useState(false);
  const [serviceMapVisible, setServiceMapVisible] = useState(false);
  const serviceCenterRef = useRef({ lat: null, lng: null });

  // ── خطوة 6: الهوية ───────────────────────────────────────────────────────
  const [idFile,      setIdFile]      = useState(null);
  const [idUploading, setIdUploading] = useState(false);
  const [idUploaded,  setIdUploaded]  = useState(false);
  const idInputRef = useRef(null);

  // ── وسائل التصوير ونوع الحساب من قاعدة البيانات ───────────────────────
  const [dbShootingMethods, setDbShootingMethods] = useState(null);
  const [dbEntityType, setDbEntityType] = useState(null);

  // ── خطوة 6: وثيقة العمل الحر (للأفراد فقط) ─────────────────────────────
  const [freelanceFile, setFreelanceFile] = useState(null);
  const [freelanceUploading, setFreelanceUploading] = useState(false);
  const [freelancePermitStatus, setFreelancePermitStatus] = useState('not_uploaded');
  const [freelancePermitUrl, setFreelancePermitUrl] = useState(null);
  const freelanceInputRef = useRef(null);

  // ── خطوة 6: تصريح الدرون (يظهر فقط إذا اختار الدرون) ────────────────────
  const [droneFile,      setDroneFile]      = useState(null);
  const [droneUploading, setDroneUploading] = useState(false);
  const [dronePermitStatus, setDronePermitStatus] = useState('not_uploaded');
  const [dronePermitUrl, setDronePermitUrl] = useState(null);
  const [dronePermitExpiresAt, setDronePermitExpiresAt] = useState(null);
  const [droneExpiryStatus, setDroneExpiryStatus] = useState('no_date');
  const droneInputRef = useRef(null);

  const activeShootingMethods = (userId || savedUserId) && dbShootingMethods
    ? dbShootingMethods
    : formData.shootingMethods;
  const hasDrone = photographerUsesDrone(activeShootingMethods);
  const activeEntityType = (userId || savedUserId) && dbEntityType
    ? dbEntityType
    : formData.entityType;
  const isIndividual = photographerIsIndividual(activeEntityType);

  // ── الشروط والأحكام ───────────────────────────────────────────────────────
  const [showTerms,   setShowTerms]   = useState(false);
  const [termsText,   setTermsText]   = useState('');
  const [termsLoading, setTermsLoading] = useState(false);

  // ── Countdown للـ OTP ─────────────────────────────────────────────────────
  useEffect(() => {
    if (countdown > 0) {
      const t = setTimeout(() => setCountdown(c => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [countdown]);

  // ── تحميل وسائل التصوير وحالة تصريح الدرون من قاعدة البيانات ───────────
  const loadProfileShootingData = useCallback(async (uid) => {
    if (!uid) return;
    try {
      const { data: profile } = await supabase
        .from('photographer_profiles')
        .select(`
          entity_type,
          shooting_methods,
          freelance_permit_url,
          freelance_permit_verified,
          freelance_permit_reviewed_at,
          freelance_permit_rejection_reason,
          drone_permit_url,
          drone_permit_verified,
          drone_permit_reviewed_at,
          drone_permit_rejection_reason,
          drone_permit_expires_at
        `)
        .eq('user_id', uid)
        .single();

      if (profile) {
        setDbShootingMethods(Array.isArray(profile.shooting_methods) ? profile.shooting_methods : []);
        setDbEntityType(profile.entity_type || 'individual');
      }

      const { data: files } = await supabase.storage
        .from('verification-docs')
        .list(uid, { limit: 20, sortBy: { column: 'created_at', order: 'desc' } });

      let freelanceProfile = profile;
      if (photographerIsIndividual(profile?.entity_type) && !profile?.freelance_permit_url) {
        const backfilledFreelance = await backfillFreelancePermitFromStorage(supabase, uid, files);
        if (backfilledFreelance) {
          freelanceProfile = { ...profile, freelance_permit_url: backfilledFreelance };
        }
      }
      setFreelancePermitUrl(freelanceProfile?.freelance_permit_url || null);
      setFreelancePermitStatus(getFreelancePermitStatus(freelanceProfile));

      let droneProfile = profile;
      if (photographerUsesDrone(profile?.shooting_methods) && !profile?.drone_permit_url) {
        const backfilledUrl = await backfillDronePermitFromStorage(supabase, uid, files);
        if (backfilledUrl) {
          droneProfile = { ...profile, drone_permit_url: backfilledUrl };
        }
      }

      const droneStatus = getDronePermitStatus(droneProfile);
      setDronePermitUrl(droneProfile?.drone_permit_url || null);
      setDronePermitStatus(droneStatus);
      setDronePermitExpiresAt(droneProfile?.drone_permit_expires_at || null);
      setDroneExpiryStatus(
        droneStatus === 'approved'
          ? getDroneExpiryStatus(droneProfile?.drone_permit_expires_at)
          : 'no_date'
      );
    } catch (err) {
      console.warn('Failed to load shooting methods from profile:', err);
    }
  }, []);

  useEffect(() => {
    const uid = userId || savedUserId;
    if (uid) loadProfileShootingData(uid);
  }, [userId, savedUserId, currentStep, loadProfileShootingData]);

  // ── حفظ التقدم في localStorage ───────────────────────────────────────────
  const persistProgress = useCallback((step, data) => {
    try {
      localStorage.setItem(WIZARD_STEP_KEY, String(step));
      if (data) {
        const { agreeToTerms: _a, ...rest } = data;
        localStorage.setItem(WIZARD_DATA_KEY, JSON.stringify(rest));
      }
    } catch (_) {}
  }, []);

  const goToStep = (step) => {
    setCurrentStep(step);
    persistProgress(step, formData);
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const clearWizardStorage = () => {
    try {
      localStorage.removeItem(WIZARD_STEP_KEY);
      localStorage.removeItem(WIZARD_DATA_KEY);
      localStorage.removeItem(WIZARD_USER_KEY);
      localStorage.removeItem(WIZARD_NOTIF_KEY);
    } catch (_) {}
  };

  // رقم الجوال المطبّع (نفس منطق photographer-registration)
  const normalizedPhone = normalizePhone(formData.phone);
  const phoneDigits = (formData.phone || '').replace(/\D/g, '');

  // ── Validation ───────────────────────────────────────────────────────────
  const validateStep1 = () => {
    if (!formData.fullName.trim())                          { setError('يرجى إدخال الاسم'); return false; }
    if (!phoneDigits || phoneDigits.length !== 10)          { setError('يرجى إدخال رقم جوال صحيح (10 أرقام)'); return false; }
    if (!normalizedPhone)                                   { setError('يرجى إدخال رقم جوال صحيح يبدأ بـ 05'); return false; }
    if (!formData.email.trim())                             { setError('يرجى إدخال البريد الإلكتروني'); return false; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)){ setError('البريد الإلكتروني غير صحيح'); return false; }
    if (!formData.entityType)                               { setError('يرجى تحديد نوع الحساب'); return false; }
    if (!formData.city)                                     { setError('يرجى اختيار المدينة'); return false; }
    if (formData.entityType === 'entity' && !(formData.businessName || '').trim()) {
      setError('يرجى إدخال اسم الاستوديو / الشركة'); return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.specializations.length)   { setError('يرجى تحديد تخصص واحد على الأقل'); return false; }
    if (!formData.shootingMethods.length)   { setError('يرجى تحديد وسيلة تصوير واحدة على الأقل'); return false; }
    const missing = formData.shootingMethods.filter(m => !(formData.shootingMethodModels?.[m] || '').trim());
    if (missing.length)                     { setError('يرجى إدخال نوع الجهاز لكل وسيلة تصوير'); return false; }
    if (!formData.equipment.length)         { setError('يرجى تحديد مستوى التعديل'); return false; }
    if (!formData.languages.length)         { setError('يرجى تحديد معدة واحدة على الأقل'); return false; }
    return true;
  };

  // ── خطوة 1 → 2 ────────────────────────────────────────────────────────────
  const handleNextStep1 = () => {
    setError('');
    if (!validateStep1()) return;
    persistProgress(2, formData);
    goToStep(2);
  };

  // ── خطوة 2 → 3 ────────────────────────────────────────────────────────────
  const handleNextStep2 = async () => {
    setError('');
    if (!validateStep2()) return;

    const uid = userId || savedUserId;
    if (uid) {
      try {
        await syncShootingMethodsToProfile(
          supabase,
          uid,
          formData.shootingMethods,
          formData.shootingMethodModels
        );
        setDbShootingMethods(formData.shootingMethods);
      } catch (err) {
        setError(err?.message || 'فشل حفظ وسائل التصوير');
        return;
      }
    }

    persistProgress(3, formData);
    goToStep(3);
  };

  // ── إرسال OTP ─────────────────────────────────────────────────────────────
  const handleSendOTP = async () => {
    if (!formData.agreeToTerms) { setError('يرجى الموافقة على الشروط والأحكام'); return; }
    if (!normalizedPhone) { setError('يرجى إدخال رقم الجوال صحيح'); return; }
    setLoading(true);
    setError('');
    try {
      const { data: existingByPhone } = await supabase
        .from('user_profiles').select('role').eq('phone', normalizedPhone).maybeSingle();
      if (existingByPhone?.role === 'customer') {
        setError('هذا الرقم مسجل كحساب عميل. لا يمكن استخدامه كمصور.');
        return;
      }
      if (existingByPhone?.role === 'admin') {
        setError('هذا الرقم مسجل كحساب مشرف.');
        return;
      }
      if (existingByPhone?.role === 'photographer') {
        setError('هذا الرقم مسجل بالفعل كمصور. يرجى تسجيل الدخول.');
        return;
      }

      if (formData.email?.trim()) {
        const { data: existingByEmail } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('email', formData.email.toLowerCase().trim())
          .maybeSingle();
        if (existingByEmail) {
          setError('هذا البريد مسجل بالفعل. استخدم رقم جوال مختلف أو سجّل الدخول.');
          return;
        }
      }

      const res = await sendOTPApi(normalizedPhone);
      if (!res?.success) {
        setError(res?.error || 'فشل إرسال كود التحقق. حاول مرة أخرى');
        return;
      }
      setMsegatId(res.id);
      setOtpCode('');
      setOtpSent(true);
      setCountdown(60);
      setToast({ type: 'success', message: 'تم إرسال كود التحقق' });
    } catch (err) {
      setError(err?.message || 'حدث خطأ أثناء إرسال الكود');
    } finally {
      setLoading(false);
    }
  };

  // ── التحقق وإنشاء الحساب ──────────────────────────────────────────────────
  const handleVerifyAndCreate = useCallback(async (codeOverride) => {
    const code = codeOverride ?? otpCode;
    if (code.length !== 4) { setError('يرجى إدخال الكود كاملاً'); return; }
    setLoading(true);
    setError('');
    try {
      setProcessingMsg('جاري التحقق من الكود...');
      if (!normalizedPhone) { setError('يرجى إدخال رقم الجوال صحيح'); return; }

      // تحويل القيم إلى labels للتخزين (نفس منطق join page)
      const specializationLabels = formData.specializations.map(v => {
        const s = SPECIALIZATIONS.find(x => x.value === v);
        return s ? s.label : v;
      });
      const equipmentLabels = formData.equipment.map(v => {
        const e = EQUIPMENT_OPTIONS.find(x => x.value === v);
        return e ? e.label : v;
      });

      setProcessingMsg('جاري إنشاء الحساب...');
      const result = await verifyOtpAndSignIn({
        phone: normalizedPhone,
        code,
        role: 'photographer',
        msegatId,
        userData: {
          full_name:             formData.fullName?.trim() || null,
          email:                 formData.email?.trim()    || null,
          phone:                 normalizedPhone,
          entity_type:           formData.entityType       || 'individual',
          business_name:         formData.businessName     || null,
          city:                  formData.city             || null,
          specializations:       specializationLabels,
          equipment_available:   equipmentLabels,
          shooting_methods:      formData.shootingMethods,
          shooting_method_models:formData.shootingMethodModels,
          languages:             formData.languages,
        },
      });

      if (result?.error) throw new Error(result.error);

      const finalizeRegistration = async (uid) => {
        // حفظ الـ userId للخطوات التالية
        setUserId(uid);
        setDbShootingMethods(formData.shootingMethods);
        setDbEntityType(formData.entityType || 'individual');
        try { localStorage.setItem(WIZARD_USER_KEY, uid); } catch (_) {}

        // إشعار المشرف — مرة واحدة فقط (نتحقق من localStorage لمنع التكرار عند التحديث)
        const notifAlreadySent = (() => { try { return localStorage.getItem(WIZARD_NOTIF_KEY) === 'true'; } catch { return false; } })();
        if (!notifAlreadySent) {
          try {
            await supabase.from('admin_notifications').insert({
              type: 'new_photographer',
              title: '📸 مصور جديد انضم للمنصة',
              message: `المصور "${formData.fullName || ''}" سجّل عبر نموذج التسجيل الموحد`,
              related_user_id: uid,
            });
            try { localStorage.setItem(WIZARD_NOTIF_KEY, 'true'); } catch (_) {}
          } catch (_) {}
        }

        if (typeof localStorage !== 'undefined') {
          localStorage.setItem('native_role_choice_completed', 'true');
        }

        setToast({ type: 'success', message: '✅ تم إنشاء الحساب بنجاح' });
        setProcessingMsg('');
        persistProgress(4, formData);
        goToStep(4);
      };

      if (result?.redirectUrl) {
        const url       = new URL(result.redirectUrl);
        const tokenHash = url.searchParams.get('token');
        if (!tokenHash) throw new Error('فشل الحصول على رمز الدخول');
        const { data: authData, error: verifyError } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: 'email' });
        if (verifyError) throw new Error(verifyError.message);
        await finalizeRegistration(authData?.user?.id);
      } else if (result?.success && result?.userId) {
        await finalizeRegistration(result.userId);
      } else {
        throw new Error('فشل التحقق');
      }
    } catch (e) {
      setError(e?.message || 'كود التحقق غير صحيح');
    } finally {
      setLoading(false);
      setProcessingMsg('');
    }
  }, [otpCode, normalizedPhone, msegatId, formData, persistProgress, goToStep]);

  const handleOtpComplete = useCallback(
    (code) => {
      if (loading || verifyInFlightRef.current) return;
      verifyInFlightRef.current = true;
      handleVerifyAndCreate(code).finally(() => {
        verifyInFlightRef.current = false;
      });
    },
    [loading, handleVerifyAndCreate]
  );

  // ── خطوة 4: رفع الصورة الشخصية ─────────────────────────────────────────
  const handleAvatarSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setToast({ type: 'error', message: 'يرجى اختيار صورة' }); return; }
    if (file.size > 5 * 1024 * 1024)    { setToast({ type: 'error', message: 'الحجم الأقصى 5MB' }); return; }
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const uploadAvatar = async (uid) => {
    if (!avatarFile || !uid) return;
    const rawExt  = (avatarFile.name.split('.').pop() || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const fileExt = ['jpg','jpeg','png','gif','webp'].includes(rawExt) ? rawExt : 'jpg';
    const filePath = `${uid}-${Date.now()}-${Math.random().toString(36).slice(2,9)}.${fileExt}`;

    const { error: upErr } = await supabase.storage.from('avatars').upload(filePath, avatarFile, { cacheControl: '3600', upsert: false });
    if (upErr) throw upErr;

    const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(filePath);
    await supabase.from('user_profiles')        .update({ avatar_url: publicUrl, updated_at: new Date().toISOString() }).eq('id', uid);
    await supabase.from('photographer_profiles').update({ avatar_url: publicUrl, updated_at: new Date().toISOString() }).eq('user_id', uid);
  };

  // ── خطوة 4: إضافة صور معرض ────────────────────────────────────────────────
  const handlePortfolioSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const valid = files.filter(f => {
      if (!f.type.startsWith('image/'))  { setToast({ type: 'warning', message: `${f.name} ليس صورة` }); return false; }
      if (f.size > 10 * 1024 * 1024)    { setToast({ type: 'warning', message: `${f.name} كبير جداً (الحد 10MB)` }); return false; }
      return true;
    });
    const newItems = valid.map(file => ({ file, preview: URL.createObjectURL(file) }));
    setPortfolioItems(prev => {
      const combined = [...prev, ...newItems];
      if (combined.length > 8) {
        setToast({ type: 'warning', message: 'الحد الأقصى 8 صور' });
        return combined.slice(0, 8);
      }
      return combined;
    });
    if (portfolioInputRef.current) portfolioInputRef.current.value = '';
  };

  const removePortfolioItem = (i) => {
    setPortfolioItems(prev => {
      URL.revokeObjectURL(prev[i].preview);
      return prev.filter((_, idx) => idx !== i);
    });
  };

  const uploadPortfolioImages = async (uid) => {
    if (!portfolioItems.length || !uid) return;
    for (const item of portfolioItems) {
      try {
        const ext      = item.file.name.split('.').pop();
        const filePath = `${uid}/${Date.now()}-${Math.random().toString(36).slice(2,9)}.${ext}`;
        const { error: upErr } = await supabase.storage.from('portfolio-images').upload(filePath, item.file, { cacheControl: '3600', upsert: false });
        if (upErr) { console.warn('Portfolio upload error:', upErr); continue; }

        const { data: { publicUrl } } = supabase.storage.from('portfolio-images').getPublicUrl(filePath);
        await supabase.from('portfolio_images').insert({
          photographer_id:  uid,
          image_url:        publicUrl,
          title:            item.file.name.replace(/\.[^/.]+$/, ''),
          description:      '',
          category:         formData.specializations?.[0] || 'عام',
          is_featured:      false,
          views:            0,
          approval_status:  'pending',
        });
      } catch (err) {
        console.warn('Portfolio item upload failed:', err);
      }
    }
  };

  // ── خطوة 4: حفظ والمضي للخطوة 5 ────────────────────────────────────────
  const handleSaveStep4 = async () => {
    if (portfolioItems.length < 3) { setError('يرجى إضافة 3 صور على الأقل لمعرض أعمالك'); return; }
    const uid = userId || savedUserId;
    if (!uid) { setError('حدث خطأ في الجلسة. يرجى إعادة التسجيل.'); return; }

    setUploadingFiles(true);
    setError('');
    try {
      setProcessingMsg('جاري رفع الصورة الشخصية...');
      if (avatarFile) await uploadAvatar(uid);

      setProcessingMsg('جاري رفع صور المعرض...');
      await uploadPortfolioImages(uid);

      setToast({ type: 'success', message: 'تم رفع الصور بنجاح' });
      persistProgress(5, formData);
      goToStep(5);
    } catch (err) {
      setError(err?.message || 'فشل رفع الصور. يرجى المحاولة مجدداً');
    } finally {
      setUploadingFiles(false);
      setProcessingMsg('');
    }
  };

  // ── خطوة 5: نطاق الخدمة ─────────────────────────────────────────────────
  const reverseGeocodeLabel = async (lat, lng) => {
    const key = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!key) return formData.city || 'الرياض';
    try {
      const res  = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&language=ar&key=${key}`);
      const data = await res.json();
      if (data.status === 'OK' && data.results?.length) {
        const { city, district } = extractCityDistrict(data.results[0]);
        return district ? `${city || formData.city} — ${district}` : (city || formData.city);
      }
    } catch (_) {}
    return formData.city || 'الرياض';
  };

  const handleServiceLocationSelect = async (locationData) => {
    const { lat, lng } = locationData;
    const cityCenter   = CITY_COORDS[formData.city] || CITY_COORDS['الرياض'];
    const dist = calculateDistance(Number(cityCenter.lat), Number(cityCenter.lng), Number(lat), Number(lng));
    if (isNaN(dist) || dist > 100) {
      setToast({ type: 'error', message: 'لا يمكن تعيين مركز نطاق الخدمة خارج مدينتك الأساسية (الحد 100 كم)' });
      return;
    }
    const label = await reverseGeocodeLabel(lat, lng);
    serviceCenterRef.current = { lat, lng };
    setServiceCenter({ lat, lng });
    setServiceLabel(label);
    setServiceMapVisible(false);
  };

  const handleUseCurrentLocation = async () => {
    if (!isGeolocationSupported()) { setToast({ type: 'error', message: 'المتصفح لا يدعم تحديد الموقع' }); return; }
    setSavingService(true);
    try {
      const pos = await getCurrentPosition({ enableHighAccuracy: true, timeout: 10000, maximumAge: 0 });
      const { latitude: lat, longitude: lng } = pos.coords;
      const cityCenter = CITY_COORDS[formData.city] || CITY_COORDS['الرياض'];
      const dist = calculateDistance(Number(cityCenter.lat), Number(cityCenter.lng), lat, lng);
      if (isNaN(dist) || dist > 100) { setToast({ type: 'error', message: 'موقعك خارج مدينتك (الحد 100 كم)' }); return; }
      const label = await reverseGeocodeLabel(lat, lng);
      serviceCenterRef.current = { lat, lng };
      setServiceCenter({ lat, lng });
      setServiceLabel(label);
    } catch { setToast({ type: 'error', message: 'فشل تحديد موقعك. حدد يدوياً على الخريطة' }); }
    finally { setSavingService(false); }
  };

  const handleSaveServiceArea = async () => {
    const { lat, lng } = serviceCenterRef.current;
    if (!lat || !lng) { setError('يرجى تحديد موقع على الخريطة'); return; }
    const uid = userId || savedUserId;
    if (!uid) { setError('حدث خطأ في الجلسة'); return; }

    setSavingService(true);
    setError('');
    try {
      const { error } = await supabase.from('photographer_profiles').update({
        service_center_lat: Number(lat),
        service_center_lng: Number(lng),
        service_radius_km:  serviceRadius,
        service_area_label: serviceLabel || null,
        updated_at: new Date().toISOString(),
      }).eq('user_id', uid);
      if (error) throw error;

      setToast({ type: 'success', message: 'تم حفظ نطاق الخدمة' });
      persistProgress(6, formData);
      goToStep(6);
    } catch (err) {
      setError(err?.message || 'فشل حفظ نطاق الخدمة');
    } finally {
      setSavingService(false);
    }
  };

  const skipServiceArea = () => {
    persistProgress(6, formData);
    goToStep(6);
  };

  // ── خطوة 6: رفع الهوية ─────────────────────────────────────────────────
  const handleIdSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/jpeg','image/png','image/webp','application/pdf'];
    if (!allowed.includes(file.type)) { setToast({ type: 'error', message: 'يُقبل JPEG, PNG, WebP أو PDF' }); return; }
    if (file.size > 10 * 1024 * 1024) { setToast({ type: 'error', message: 'الحجم الأقصى 10MB' }); return; }
    setIdFile(file);
    // رفع تلقائي فور اختيار الملف
    await handleUploadId(file);
  };

  const handleUploadId = async (fileArg) => {
    const file = fileArg || idFile;
    if (!file) { setToast({ type: 'error', message: 'يرجى اختيار ملف الهوية' }); return; }
    const uid = userId || savedUserId;
    if (!uid) { setError('حدث خطأ في الجلسة'); return; }

    setIdUploading(true);
    setError('');
    try {
      const ext      = file.name.split('.').pop();
      const filePath = `${uid}/id_document_${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage.from('verification-docs').upload(filePath, file, { cacheControl: '3600', upsert: false });
      if (upErr) throw upErr;

      const { data: { publicUrl } } = supabase.storage.from('verification-docs').getPublicUrl(filePath);
      await supabase.from('photographer_profiles').update({
        id_document_url:           publicUrl,
        id_document_type:          file.type.includes('pdf') ? 'pdf' : 'image',
        verification_status:       'submitted',
        verification_submitted_at: new Date().toISOString(),
        identity_verified:         false,
        identity_document_expires_at: null,
        updated_at:                new Date().toISOString(),
      }).eq('user_id', uid);

      // إشعار المشرف
      try {
        await supabase.from('admin_notifications').insert({
          type:  'verification_request',
          title: '📄 طلب توثيق هوية جديد',
          message: `المصور "${formData.fullName}" رفع مستند تحقق من الهوية`,
          related_user_id: uid,
        });
      } catch (_) {}

      setIdUploaded(true);
      setToast({ type: 'success', message: '✅ تم رفع الهوية بنجاح' });
    } catch (err) {
      setError(err?.message || 'فشل رفع الهوية');
    } finally {
      setIdUploading(false);
    }
  };

  // ── خطوة 6: رفع وثيقة العمل الحر ────────────────────────────────────────
  const handleFreelanceSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validationError = validateFreelancePermitFile(file);
    if (validationError) {
      setToast({ type: 'error', message: validationError });
      return;
    }
    setFreelanceFile(file);
    await handleUploadFreelancePermit(file);
  };

  const handleUploadFreelancePermit = async (fileArg) => {
    const file = fileArg || freelanceFile;
    if (!file) return;
    const uid = userId || savedUserId;
    if (!uid) return;
    setFreelanceUploading(true);
    try {
      const { publicUrl } = await uploadFreelancePermit(supabase, {
        userId: uid,
        file,
        photographerName: formData.fullName?.trim() || 'مصور',
        previousUrl: freelancePermitUrl,
      });
      setFreelancePermitUrl(publicUrl);
      setFreelancePermitStatus('submitted');
      setToast({ type: 'success', message: '✅ تم رفع وثيقة العمل الحر وإرسالها للمراجعة' });
    } catch (err) {
      setToast({ type: 'error', message: err?.message || 'فشل رفع الوثيقة' });
    } finally {
      setFreelanceUploading(false);
    }
  };

  // ── خطوة 6: رفع تصريح الدرون ────────────────────────────────────────────
  const handleDroneSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validationError = validateDronePermitFile(file);
    if (validationError) {
      setToast({ type: 'error', message: validationError });
      return;
    }
    setDroneFile(file);
    await handleUploadDronePermit(file);
  };

  const handleUploadDronePermit = async (fileArg) => {
    const file = fileArg || droneFile;
    if (!file) return;
    const uid = userId || savedUserId;
    if (!uid) return;
    setDroneUploading(true);
    try {
      const { publicUrl } = await uploadDronePermit(supabase, {
        userId: uid,
        file,
        photographerName: formData.fullName?.trim() || 'مصور',
        previousUrl: dronePermitUrl,
      });
      setDronePermitUrl(publicUrl);
      setDronePermitStatus('submitted');
      setDronePermitExpiresAt(null);
      setDroneExpiryStatus('no_date');
      setToast({ type: 'success', message: '✅ تم رفع تصريح الدرون وإرساله للمراجعة' });
    } catch (err) {
      setToast({ type: 'error', message: err?.message || 'فشل رفع التصريح' });
    } finally {
      setDroneUploading(false);
    }
  };

  // ── إنهاء الـ Wizard ─────────────────────────────────────────────────────
  const handleFinish = () => {
    clearWizardStorage();
    navigate('/photographer-dashboard', { replace: true });
  };

  // ── فتح الشروط والأحكام ─────────────────────────────────────────────────
  const openTerms = async () => {
    setShowTerms(true);
    if (termsText) return;
    setTermsLoading(true);
    try {
      const { data } = await supabase.from('app_settings').select('value').eq('key','page_terms_content').single();
      setTermsText(data?.value || '');
    } catch { setTermsText(''); }
    finally { setTermsLoading(false); }
  };

  // ─── Step Indicator ─────────────────────────────────────────────────────────
  const STEP_LABELS = ['البيانات', 'التصوير', 'التحقق', 'الصور', 'النطاق', 'الهوية'];

  // ─── JSX ─────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50" dir="rtl" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>

      {/* ── Header ── */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-30" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <MashhadLogo variant="dark" size="sm" logoType="footer" />
          <div className="flex flex-col items-end gap-1">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="p-1 rounded-full hover:bg-slate-100 transition-colors self-end"
              aria-label="إغلاق"
            >
              <AppIcon name="X" size={16} className="text-slate-400" />
            </button>
            <button
              type="button"
              onClick={() => navigate('/photographer-login')}
              className="text-sm text-slate-500 hover:text-primary transition border-0 bg-transparent cursor-pointer"
            >
              لديك حساب؟ <span className="font-bold text-primary">سجّل الدخول</span>
            </button>
          </div>
        </div>

        {/* ── Step Progress Bar ── */}
        <div className="max-w-2xl mx-auto px-4 pb-3">
          <div className="flex items-center gap-1">
            {STEP_LABELS.map((label, i) => {
              const stepNum = i + 1;
              const done    = stepNum < currentStep;
              const active  = stepNum === currentStep;
              return (
                <div key={stepNum} className="flex-1 flex flex-col items-center gap-1">
                  <div className={`w-full h-1.5 rounded-full transition-all ${done ? 'bg-green-500' : active ? 'bg-primary' : 'bg-slate-200'}`} />
                  <span className={`text-[10px] font-medium hidden sm:block ${active ? 'text-primary' : done ? 'text-green-600' : 'text-slate-400'}`}>
                    {done ? '✓' : stepNum}. {label}
                  </span>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-slate-500 text-center mt-1">
            خطوة {currentStep} من {TOTAL_STEPS}
          </p>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="max-w-2xl mx-auto px-4 py-6">

        {/* رسالة استعادة التقدم */}
        {savedStep > 1 && currentStep === savedStep && (
          <div className="mb-4 flex items-center gap-2 bg-blue-50 border border-blue-200 text-blue-700 rounded-xl px-4 py-3 text-sm">
            <AppIcon name="Info" size={16} className="shrink-0" />
            تم استعادة تقدمك السابق — تابع من حيث توقفت
          </div>
        )}

        {error && (
          <div className="mb-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
            <AppIcon name="AlertCircle" size={16} className="shrink-0" />
            {error}
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            خطوة 1 — البيانات الأساسية
        ════════════════════════════════════════════════════════════════════ */}
        {currentStep === 1 && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-5">
            <h2 className="text-xl font-bold text-slate-900">البيانات الأساسية</h2>

            {/* الاسم */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">الاسم الكامل *</label>
              <input
                value={formData.fullName}
                onChange={e => setFormData(p => ({ ...p, fullName: e.target.value }))}
                placeholder="محمد أحمد"
                className="w-full px-4 py-3 border-2 border-slate-200 focus:border-primary rounded-xl text-sm focus:outline-none transition"
              />
            </div>

            {/* الجوال */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">رقم الجوال *</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={e => setFormData(p => ({ ...p, phone: e.target.value.replace(/\D/g, '').slice(0, 15) }))}
                inputMode="numeric"
                placeholder="05xxxxxxxx"
                dir="ltr"
                className={`w-full px-4 py-3 rounded-xl border-2 text-left focus:outline-none transition tracking-widest ${
                  (phoneDigits.length === 10 || !formData.phone)
                    ? 'border-slate-200 focus:border-primary'
                    : 'border-red-500 focus:border-red-500'
                }`}
              />
            </div>

            {/* الإيميل */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">البريد الإلكتروني *</label>
              <input
                value={formData.email}
                onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                type="email" placeholder="email@example.com" dir="ltr"
                className="w-full px-4 py-3 border-2 border-slate-200 focus:border-primary rounded-xl text-sm focus:outline-none transition"
              />
            </div>

            {/* المدينة */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">المدينة *</label>
              <select
                value={formData.city}
                onChange={e => setFormData(p => ({ ...p, city: e.target.value }))}
                className="select-rtl w-full py-3 border-2 border-slate-200 focus:border-primary rounded-xl text-sm focus:outline-none transition bg-white"
              >
                <option value="">اختر مدينتك</option>
                {CITIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            {/* نوع الحساب */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-3">نوع الحساب *</label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { val: 'individual', label: 'مصور فرد',         icon: 'User' },
                  { val: 'entity',     label: 'استوديو / شركة',   icon: 'Building', soon: true },
                ].map(opt => (
                  <div key={opt.val} className={`relative ${opt.soon ? 'cursor-not-allowed opacity-60' : ''}`}>
                    <button
                      type="button"
                      disabled={opt.soon}
                      onClick={() => !opt.soon && setFormData(p => ({ ...p, entityType: opt.val }))}
                      className={`w-full p-4 rounded-2xl border-2 flex flex-col items-center gap-2 transition-all ${
                        formData.entityType === opt.val
                          ? 'border-primary bg-primary/5 shadow-sm'
                          : 'border-slate-200 hover:border-primary/40'
                      }`}
                    >
                      <AppIcon name={opt.icon} size={22} className={formData.entityType === opt.val ? 'text-primary' : 'text-slate-400'} />
                      <span className="text-sm font-medium text-slate-700">{opt.label}</span>
                    </button>
                    {opt.soon && (
                      <span className="absolute -top-2 -right-2 text-[10px] font-bold bg-amber-400 text-white px-1.5 py-0.5 rounded-full">قريباً</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* اسم الاستوديو */}
            {formData.entityType === 'entity' && (
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">اسم الاستوديو / الشركة *</label>
                <input
                  value={formData.businessName}
                  onChange={e => setFormData(p => ({ ...p, businessName: e.target.value }))}
                  placeholder="استوديو مشهد"
                  className="w-full px-4 py-3 border-2 border-slate-200 focus:border-primary rounded-xl text-sm focus:outline-none transition"
                />
              </div>
            )}

            <button
              type="button"
              onClick={handleNextStep1}
              className="w-full py-4 bg-primary text-white font-bold rounded-xl hover:opacity-90 transition flex items-center justify-center gap-2 border-0"
            >
              التالي
              <AppIcon name="ChevronLeft" size={20} />
            </button>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            خطوة 2 — التخصصات ووسائل التصوير
        ════════════════════════════════════════════════════════════════════ */}
        {currentStep === 2 && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-6">
            <h2 className="text-xl font-bold text-slate-900">تخصصاتك ووسائل التصوير</h2>

            {/* التخصصات */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-3">تخصصاتك في التصوير *</label>
              <div className="grid grid-cols-2 gap-2">
                {SPECIALIZATIONS.map(s => {
                  const sel = formData.specializations.includes(s.value);
                  return (
                    <button
                      key={s.value} type="button"
                      onClick={() => setFormData(p => ({
                        ...p,
                        specializations: sel ? p.specializations.filter(v => v !== s.value) : [...p.specializations, s.value]
                      }))}
                      className={`p-3 rounded-xl border-2 text-sm text-right flex items-center gap-2 transition-all ${
                        sel ? 'border-primary bg-primary/5 text-primary font-semibold' : 'border-slate-200 hover:border-primary/40 text-slate-700'
                      }`}
                    >
                      {sel && <AppIcon name="Check" size={14} className="shrink-0" />}
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="border-t border-slate-100" />

            {/* وسائل التصوير */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-3">وسائل التصوير *</label>
              <div className="space-y-3">
                {SHOOTING_METHODS.map(m => {
                  const sel = formData.shootingMethods.includes(m.value);
                  return (
                    <div key={m.value} className={`rounded-xl border-2 transition-all ${sel ? 'border-primary bg-primary/5' : 'border-slate-200'}`}>
                      <button
                        type="button"
                        onClick={() => setFormData(p => ({
                          ...p,
                          shootingMethods: sel
                            ? p.shootingMethods.filter(v => v !== m.value)
                            : [...p.shootingMethods, m.value]
                        }))}
                        className="w-full p-3 flex items-center gap-3 text-right"
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${sel ? 'bg-primary text-white' : 'bg-slate-100 text-slate-400'}`}>
                          {sel ? <AppIcon name="Check" size={16} /> : <AppIcon name={m.icon} size={16} />}
                        </div>
                        <span className={`text-sm font-medium ${sel ? 'text-primary' : 'text-slate-700'}`}>{m.label}</span>
                      </button>
                      {sel && (
                        <div className="px-3 pb-3">
                          <input
                            value={formData.shootingMethodModels[m.value] || ''}
                            onChange={e => setFormData(p => ({
                              ...p,
                              shootingMethodModels: { ...p.shootingMethodModels, [m.value]: e.target.value }
                            }))}
                            placeholder={m.placeholder}
                            className="w-full px-3 py-2 border border-slate-200 focus:border-primary rounded-lg text-sm focus:outline-none transition"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="border-t border-slate-100" />

            {/* مستوى التعديل */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-3">مستوى التعديل الذي تقدمه *</label>
              <div className="grid grid-cols-2 gap-3">
                {EQUIPMENT_OPTIONS.map(opt => {
                  const sel = formData.equipment[0] === opt.value;
                  return (
                    <button
                      key={opt.value} type="button"
                      onClick={() => setFormData(p => ({ ...p, equipment: [opt.value] }))}
                      className={`p-4 rounded-xl border-2 text-sm text-center font-medium transition-all ${
                        sel ? 'border-primary bg-primary/5 text-primary' : 'border-slate-200 hover:border-primary/40 text-slate-700'
                      }`}
                    >
                      {sel && <AppIcon name="Check" size={14} className="inline ml-1" />}
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="border-t border-slate-100" />

            {/* المعدات المتاحة */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-3">المعدات المتاحة لديك *</label>
              <div className="flex flex-wrap gap-3">
                {AVAILABLE_EQUIPMENT.map(eq => {
                  const checked = formData.languages.includes(eq.value);
                  return (
                    <label key={eq.value} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={e => setFormData(p => ({
                          ...p,
                          languages: e.target.checked
                            ? [...p.languages, eq.value]
                            : p.languages.filter(l => l !== eq.value)
                        }))}
                        className="w-4 h-4 text-primary border-slate-300 rounded focus:ring-primary"
                      />
                      <span className="text-sm text-slate-700">{eq.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => goToStep(1)}
                className="flex-1 py-3 bg-slate-100 text-slate-700 font-bold rounded-xl hover:bg-slate-200 transition flex items-center justify-center gap-2 border-0"
              >
                <AppIcon name="ChevronRight" size={20} />
                رجوع
              </button>
              <button
                type="button"
                onClick={handleNextStep2}
                className="flex-1 py-3 bg-primary text-white font-bold rounded-xl hover:opacity-90 transition flex items-center justify-center gap-2 border-0"
              >
                التالي
                <AppIcon name="ChevronLeft" size={20} />
              </button>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            خطوة 3 — التحقق من الجوال (OTP)
        ════════════════════════════════════════════════════════════════════ */}
        {currentStep === 3 && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-5">
            <h2 className="text-xl font-bold text-slate-900">التحقق من رقم الجوال</h2>

            {!otpSent ? (
              <>
                <div className="bg-slate-50 rounded-xl p-4 text-sm text-slate-600 space-y-1">
                  <p>سيتم إرسال كود تحقق إلى:</p>
                  <p className="font-bold text-slate-900" dir="ltr">{normalizedPhone || formData.phone}</p>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-4">
                  <label className="flex items-start gap-3 cursor-pointer text-sm text-slate-700 leading-relaxed">
                    <input
                      type="checkbox"
                      checked={formData.agreeToTerms}
                      onChange={e => setFormData(p => ({ ...p, agreeToTerms: e.target.checked }))}
                      className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300 text-primary focus:ring-primary"
                    />
                    <span>
                      أوافق على{' '}
                      <button
                        type="button"
                        onClick={openTerms}
                        className="font-bold text-primary hover:underline inline p-0 border-0 bg-transparent cursor-pointer"
                      >
                        الشروط والأحكام
                      </button>
                    </span>
                  </label>
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => goToStep(2)}
                    className="py-3 px-6 bg-slate-100 text-slate-700 font-bold rounded-xl hover:bg-slate-200 transition flex items-center gap-2 border-0"
                  >
                    <AppIcon name="ChevronRight" size={20} />
                    رجوع
                  </button>
                  <button
                    type="button"
                    onClick={handleSendOTP}
                    disabled={loading || !formData.agreeToTerms || !normalizedPhone}
                    className="flex-1 py-3 bg-primary text-white font-bold rounded-xl hover:opacity-90 transition disabled:opacity-50 flex items-center justify-center gap-2 border-0"
                  >
                    {loading
                      ? <><AppIcon name="Loader" size={20} className="animate-spin" />جاري الإرسال...</>
                      : <><AppIcon name="Send" size={20} />إرسال كود التحقق</>}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-slate-500 text-sm text-center">
                  أدخل الكود المرسل إلى <span className="font-bold text-slate-700" dir="ltr">{normalizedPhone || formData.phone}</span>
                </p>

                <OtpInput
                  value={otpCode}
                  onChange={setOtpCode}
                  onComplete={handleOtpComplete}
                  disabled={loading}
                  autoFocus={otpSent}
                  boxClassName="w-14 h-16 text-center text-2xl font-bold rounded-xl border-2 border-slate-200 focus:border-primary focus:outline-none transition"
                />

                <button
                  type="button"
                  onClick={handleVerifyAndCreate}
                  disabled={loading || otpCode.length !== 4}
                  className="w-full py-4 bg-primary text-white font-bold rounded-xl hover:opacity-90 transition disabled:opacity-50 flex items-center justify-center gap-2 border-0"
                >
                  {loading
                    ? <><AppIcon name="Loader" size={20} className="animate-spin" />{processingMsg || 'جاري التسجيل...'}</>
                    : <><AppIcon name="Check" size={20} />إنشاء الحساب</>}
                </button>

                <div className="text-center text-sm">
                  {countdown > 0
                    ? <p className="text-slate-500">إعادة الإرسال بعد <span className="font-bold text-primary">{countdown}</span> ثانية</p>
                    : <button type="button" onClick={handleSendOTP} className="text-primary font-bold hover:underline border-0 bg-transparent cursor-pointer">إعادة إرسال الكود</button>}
                </div>
              </>
            )}
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            خطوة 4 — الصورة الشخصية ومعرض الأعمال
        ════════════════════════════════════════════════════════════════════ */}
        {currentStep === 4 && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-6">
            <div>
              <h2 className="text-xl font-bold text-slate-900">الصورة الشخصية ومعرض الأعمال</h2>
              <p className="text-sm text-slate-500 mt-1">الصور تُراجَع من قِبل المشرف قبل الظهور في نتائج البحث</p>
            </div>

            {/* الصورة الشخصية */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-3">الصورة الشخصية</label>
              <div className="flex items-center gap-4">
                <div
                  className="w-20 h-20 rounded-full border-2 border-dashed border-slate-300 overflow-hidden flex items-center justify-center bg-slate-50 cursor-pointer hover:border-primary transition flex-shrink-0"
                  onClick={() => avatarInputRef.current?.click()}
                >
                  {avatarPreview
                    ? <img src={avatarPreview} alt="avatar" className="w-full h-full object-cover" />
                    : <AppIcon name="User" size={32} className="text-slate-300" />}
                </div>
                <div className="flex-1">
                  <button
                    type="button"
                    onClick={() => avatarInputRef.current?.click()}
                    className="py-2 px-4 bg-slate-100 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-200 transition border-0"
                  >
                    {avatarPreview ? 'تغيير الصورة' : 'رفع صورة شخصية'}
                  </button>
                  <p className="text-xs text-slate-400 mt-1">PNG, JPG — الحد الأقصى 5MB</p>
                </div>
              </div>
              <input ref={avatarInputRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarSelect} />
            </div>

            <div className="border-t border-slate-100" />

            {/* معرض الأعمال */}
            <div>
              <div className="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-xl px-3 py-2 mb-3 text-xs text-blue-700">
                <AppIcon name="Lightbulb" size={14} className="shrink-0" />
                <span>نصيحة للعرض الأمثل: استخدم صوراً أفقية بدقة عالية — تظهر أفضل في بطاقتك أمام العملاء.</span>
              </div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-semibold text-slate-700">
                  معرض الأعمال *
                  <span className={`mr-2 text-xs font-normal px-2 py-0.5 rounded-full ${
                    portfolioItems.length >= 3 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {portfolioItems.length}/3 صور مطلوبة
                  </span>
                </label>
                {portfolioItems.length < 8 && (
                  <button
                    type="button"
                    onClick={() => portfolioInputRef.current?.click()}
                    className="text-sm text-primary font-medium hover:underline border-0 bg-transparent cursor-pointer flex items-center gap-1"
                  >
                    <AppIcon name="Plus" size={16} />
                    إضافة صور
                  </button>
                )}
              </div>

              {portfolioItems.length === 0 ? (
                <div
                  onClick={() => portfolioInputRef.current?.click()}
                  className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center cursor-pointer hover:border-primary hover:bg-primary/5 transition"
                >
                  <AppIcon name="Image" size={40} className="text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-500 font-medium">اضغط لإضافة صور أعمالك</p>
                  <p className="text-xs text-slate-400 mt-1">PNG, JPG — الحد الأقصى 10MB لكل صورة</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {portfolioItems.map((item, i) => (
                    <div key={i} className="relative aspect-square rounded-xl overflow-hidden bg-slate-100">
                      <img src={item.preview} alt="" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removePortfolioItem(i)}
                        className="absolute top-1 left-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition border-0"
                      >
                        <AppIcon name="X" size={12} />
                      </button>
                    </div>
                  ))}
                  {portfolioItems.length < 8 && (
                    <div
                      onClick={() => portfolioInputRef.current?.click()}
                      className="aspect-square rounded-xl border-2 border-dashed border-slate-200 flex items-center justify-center cursor-pointer hover:border-primary hover:bg-primary/5 transition"
                    >
                      <AppIcon name="Plus" size={24} className="text-slate-400" />
                    </div>
                  )}
                </div>
              )}
              <input ref={portfolioInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handlePortfolioSelect} />
            </div>

            <button
              type="button"
              onClick={handleSaveStep4}
              disabled={uploadingFiles || portfolioItems.length < 3}
              className="w-full py-4 bg-primary text-white font-bold rounded-xl hover:opacity-90 transition disabled:opacity-50 flex items-center justify-center gap-2 border-0"
            >
              {uploadingFiles
                ? <><AppIcon name="Loader" size={20} className="animate-spin" />{processingMsg || 'جاري الرفع...'}</>
                : <><AppIcon name="Upload" size={20} />رفع الصور والمتابعة</>}
            </button>

            {portfolioItems.length < 3 && (
              <p className="text-xs text-amber-600 text-center">
                ⚠️ أضف {3 - portfolioItems.length} {3 - portfolioItems.length === 1 ? 'صورة' : 'صور'} على الأقل للمتابعة
              </p>
            )}
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            خطوة 5 — نطاق الخدمة
        ════════════════════════════════════════════════════════════════════ */}
        {currentStep === 5 && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-5">
            <div>
              <h2 className="text-xl font-bold text-slate-900">نطاق الخدمة</h2>
              <p className="text-sm text-slate-500 mt-1">حدد المنطقة التي تقدم فيها خدماتك — ضمن {formData.city || 'مدينتك'}</p>
            </div>

            {/* إجراءات تحديد الموقع */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setServiceMapVisible(v => !v)}
                className="flex-1 py-3 bg-slate-100 text-slate-700 font-semibold rounded-xl hover:bg-slate-200 transition flex items-center justify-center gap-2 border-0"
              >
                <AppIcon name="Map" size={18} />
                {serviceMapVisible ? 'إخفاء الخريطة' : 'تحديد على الخريطة'}
              </button>
              <button
                type="button"
                onClick={handleUseCurrentLocation}
                disabled={savingService}
                className="flex-1 py-3 bg-primary/10 text-primary font-semibold rounded-xl hover:bg-primary/20 transition disabled:opacity-50 flex items-center justify-center gap-2 border-0"
              >
                {savingService
                  ? <AppIcon name="Loader" size={18} className="animate-spin" />
                  : <AppIcon name="Navigation" size={18} />}
                موقعي الحالي
              </button>
            </div>

            {/* الخريطة inline — بدون modal */}
            {serviceMapVisible && (
              <LocationPickerMap
                key={`wizard-map-${serviceCenter.lat || 'default'}-${serviceCenter.lng || 'default'}`}
                initialCenter={
                  serviceCenter.lat && serviceCenter.lng
                    ? { lat: serviceCenter.lat, lng: serviceCenter.lng }
                    : CITY_COORDS[formData.city] || { lat: 24.7136, lng: 46.6753 }
                }
                onLocationSelect={handleServiceLocationSelect}
                onCancel={() => setServiceMapVisible(false)}
              />
            )}

            {/* حالة الموقع المحدد */}
            {serviceCenter.lat && (
              <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl p-4">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <AppIcon name="MapPin" size={20} className="text-green-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-green-800">تم تحديد الموقع</p>
                  {serviceLabel && <p className="text-xs text-green-600 mt-0.5">{serviceLabel}</p>}
                </div>
              </div>
            )}

            {/* نصف القطر */}
            {serviceCenter.lat && (
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  نطاق الخدمة: <span className="text-primary">{serviceRadius} كم</span>
                </label>
                <input
                  type="range" min={5} max={50} step={5}
                  value={serviceRadius}
                  onChange={e => setServiceRadius(Number(e.target.value))}
                  className="w-full accent-primary"
                />
                <div className="flex justify-between text-xs text-slate-400 mt-1">
                  <span>5 كم</span><span>50 كم</span>
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={skipServiceArea}
                className="flex-1 py-3 bg-slate-100 text-slate-600 font-medium rounded-xl hover:bg-slate-200 transition border-0"
              >
                تخطي الآن
              </button>
              <button
                type="button"
                onClick={handleSaveServiceArea}
                disabled={savingService || !serviceCenter.lat}
                className="flex-1 py-3 bg-primary text-white font-bold rounded-xl hover:opacity-90 transition disabled:opacity-50 flex items-center justify-center gap-2 border-0"
              >
                {savingService
                  ? <AppIcon name="Loader" size={18} className="animate-spin" />
                  : <AppIcon name="Check" size={18} />}
                حفظ والمتابعة
              </button>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            خطوة 6 — توثيق الهوية (اختياري)
        ════════════════════════════════════════════════════════════════════ */}
        {currentStep === 6 && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-5">
            <div>
              <h2 className="text-xl font-bold text-slate-900">توثيق الهوية</h2>
              <p className="text-sm text-slate-500 mt-1">ارفع صورة هويتك الوطنية أو جواز السفر لإتمام التوثيق. يمكنك رفع التصاريح والشهادات المهنية لاحقاً من قسم "التحقق" في لوحة التحكم.</p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-700 flex items-start gap-3">
              <AppIcon name="Info" size={18} className="shrink-0 mt-0.5" />
              <p>التوثيق يتيح لك الظهور في نتائج البحث بعد مراجعة المشرف. يمكنك رفعه الآن أو لاحقاً من لوحة التحكم.</p>
            </div>

            {idUploaded ? (
              <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl p-4">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <AppIcon name="CheckCircle" size={22} className="text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-green-800">تم رفع الهوية بنجاح</p>
                  <p className="text-xs text-green-600 mt-0.5">سيتم مراجعتها من قِبل المشرف خلال 24-48 ساعة</p>
                </div>
              </div>
            ) : (
              <div>
                <div
                  onClick={() => !idUploading && idInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-6 text-center transition ${
                    idUploading
                      ? 'border-primary bg-primary/5 cursor-wait'
                      : idFile
                        ? 'border-primary bg-primary/5 cursor-pointer'
                        : 'border-slate-200 hover:border-primary hover:bg-primary/5 cursor-pointer'
                  }`}
                >
                  {idUploading ? (
                    <>
                      <AppIcon name="Loader" size={36} className="mx-auto mb-2 text-primary animate-spin" />
                      <p className="text-sm font-medium text-primary">جاري رفع الهوية...</p>
                    </>
                  ) : idFile ? (
                    <>
                      <AppIcon name="FileCheck" size={36} className="mx-auto mb-2 text-primary" />
                      <p className="text-sm font-medium text-slate-700">{idFile.name}</p>
                      <p className="text-xs text-slate-400 mt-1">اضغط لتغيير الملف</p>
                    </>
                  ) : (
                    <>
                      <AppIcon name="FileUp" size={36} className="mx-auto mb-2 text-slate-300" />
                      <p className="text-sm font-medium text-slate-700">اضغط لاختيار ملف الهوية</p>
                      <p className="text-xs text-slate-400 mt-1">JPEG, PNG, WebP أو PDF — الحد الأقصى 10MB</p>
                    </>
                  )}
                </div>
                <input ref={idInputRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={handleIdSelect} />
              </div>
            )}

            {/* وثيقة العمل الحر — للمصور الفرد فقط */}
            {isIndividual && (
              <div className="border border-indigo-200 bg-indigo-50 rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <AppIcon name="Briefcase" size={18} className="text-indigo-600 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-indigo-800">وثيقة العمل الحر</p>
                    <p className="text-xs text-indigo-600 mt-0.5">يرجى رفع وثيقة العمل الحر الصادرة من الجهة المختصة</p>
                  </div>
                </div>

                {freelancePermitUrl ? (
                  <div className={`flex items-center gap-2 rounded-lg p-3 border ${
                    freelancePermitStatus === 'approved' ? 'bg-green-50 border-green-200' :
                    freelancePermitStatus === 'rejected' ? 'bg-red-50 border-red-200' :
                    'bg-blue-50 border-blue-200'
                  }`}>
                    <AppIcon
                      name={freelancePermitStatus === 'approved' ? 'CheckCircle' : freelancePermitStatus === 'rejected' ? 'XCircle' : 'Clock'}
                      size={18}
                      className={
                        freelancePermitStatus === 'approved' ? 'text-green-600' :
                        freelancePermitStatus === 'rejected' ? 'text-red-600' :
                        'text-blue-600'
                      }
                    />
                    <p className="text-sm font-medium text-slate-800">
                      {getFreelancePermitStatusLabel(freelancePermitStatus)}
                    </p>
                    <button
                      type="button"
                      onClick={() => !freelanceUploading && freelanceInputRef.current?.click()}
                      className="mr-auto text-xs text-slate-500 underline underline-offset-2 hover:text-slate-700 border-0 bg-transparent cursor-pointer"
                    >
                      استبدال
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => !freelanceUploading && freelanceInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-xl p-4 text-center transition cursor-pointer ${
                      freelanceUploading ? 'border-indigo-400 bg-indigo-50 cursor-wait' :
                      freelanceFile ? 'border-indigo-400 bg-indigo-50' :
                      'border-indigo-200 hover:border-indigo-400 hover:bg-indigo-50'
                    }`}
                  >
                    {freelanceUploading ? (
                      <>
                        <AppIcon name="Loader" size={28} className="mx-auto mb-1 text-indigo-500 animate-spin" />
                        <p className="text-sm font-medium text-indigo-700">جاري رفع الوثيقة...</p>
                      </>
                    ) : freelanceFile ? (
                      <>
                        <AppIcon name="FileCheck" size={28} className="mx-auto mb-1 text-indigo-500" />
                        <p className="text-sm font-medium text-slate-700">{freelanceFile.name}</p>
                        <p className="text-xs text-slate-400 mt-0.5">اضغط لتغيير الملف</p>
                      </>
                    ) : (
                      <>
                        <AppIcon name="FileUp" size={28} className="mx-auto mb-1 text-indigo-300" />
                        <p className="text-sm font-medium text-slate-700">اضغط لرفع وثيقة العمل الحر</p>
                        <p className="text-xs text-slate-400 mt-0.5">JPEG, PNG, WebP أو PDF — الحد الأقصى 10MB</p>
                      </>
                    )}
                  </div>
                )}
                <input ref={freelanceInputRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={handleFreelanceSelect} />
              </div>
            )}

            {/* تصريح الدرون — يظهر فقط إذا اختار الدرون كوسيلة تصوير */}
            {hasDrone && (
              <div className="border border-amber-200 bg-amber-50 rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <AppIcon name="FileText" size={18} className="text-amber-600 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-amber-800">تصريح الطائرة المسيّرة (درون)</p>
                    <p className="text-xs text-amber-600 mt-0.5">لاحظنا أنك تستخدم الدرون — يرجى رفع تصريح التشغيل الصادر من الجهة المختصة</p>
                  </div>
                </div>

                {dronePermitUrl ? (
                  <div className={`flex items-center gap-2 rounded-lg p-3 border ${
                    dronePermitStatus === 'approved' ? 'bg-green-50 border-green-200' :
                    dronePermitStatus === 'rejected' ? 'bg-red-50 border-red-200' :
                    'bg-blue-50 border-blue-200'
                  }`}>
                    <AppIcon
                      name={dronePermitStatus === 'approved' ? 'CheckCircle' : dronePermitStatus === 'rejected' ? 'XCircle' : 'Clock'}
                      size={18}
                      className={
                        dronePermitStatus === 'approved' ? 'text-green-600' :
                        dronePermitStatus === 'rejected' ? 'text-red-600' :
                        'text-blue-600'
                      }
                    />
                    <p className="text-sm font-medium text-slate-800">
                      {getDronePermitStatusLabel(dronePermitStatus)}
                    </p>
                    <button
                      type="button"
                      onClick={() => !droneUploading && droneInputRef.current?.click()}
                      className="mr-auto text-xs text-slate-500 underline underline-offset-2 hover:text-slate-700 border-0 bg-transparent cursor-pointer"
                    >
                      استبدال
                    </button>
                  </div>
                ) : null}

                {dronePermitStatus === 'approved' && dronePermitExpiresAt && (
                  <div className={`flex items-center gap-2 rounded-lg p-3 border ${getDroneExpiryBadgeClass(droneExpiryStatus)}`}>
                    <AppIcon name="Calendar" size={16} className="shrink-0" />
                    <p className="text-sm">
                      ينتهي في {new Date(dronePermitExpiresAt).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'long', year: 'numeric' })}
                      {' — '}
                      <span className="font-semibold">{getDroneExpiryStatusLabel(droneExpiryStatus)}</span>
                    </p>
                  </div>
                )}

                {!dronePermitUrl && (
                  <div
                    onClick={() => !droneUploading && droneInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-xl p-4 text-center transition cursor-pointer ${
                      droneUploading ? 'border-amber-400 bg-amber-50 cursor-wait' :
                      droneFile ? 'border-amber-400 bg-amber-50' :
                      'border-amber-200 hover:border-amber-400 hover:bg-amber-50'
                    }`}
                  >
                    {droneUploading ? (
                      <>
                        <AppIcon name="Loader" size={28} className="mx-auto mb-1 text-amber-500 animate-spin" />
                        <p className="text-sm font-medium text-amber-700">جاري رفع التصريح...</p>
                      </>
                    ) : droneFile ? (
                      <>
                        <AppIcon name="FileCheck" size={28} className="mx-auto mb-1 text-amber-500" />
                        <p className="text-sm font-medium text-slate-700">{droneFile.name}</p>
                        <p className="text-xs text-slate-400 mt-0.5">اضغط لتغيير الملف</p>
                      </>
                    ) : (
                      <>
                        <AppIcon name="FileUp" size={28} className="mx-auto mb-1 text-amber-300" />
                        <p className="text-sm font-medium text-slate-700">اضغط لرفع التصريح</p>
                        <p className="text-xs text-slate-400 mt-0.5">JPEG, PNG, WebP أو PDF — الحد الأقصى 10MB</p>
                      </>
                    )}
                  </div>
                )}
                <input ref={droneInputRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={handleDroneSelect} />
              </div>
            )}

            <div className="border-t border-slate-100 pt-4">
              <button
                type="button"
                onClick={handleFinish}
                className="w-full py-4 bg-green-600 text-white font-bold rounded-xl hover:bg-green-700 transition flex items-center justify-center gap-2 border-0"
              >
                <AppIcon name="Rocket" size={20} />
                {idUploaded ? 'الذهاب إلى لوحة التحكم' : 'أكمل لاحقاً'}
              </button>
              {!idUploaded && (
                <p className="text-xs text-slate-400 text-center mt-2">يمكنك رفع الهوية لاحقاً من قسم "التحقق" في حسابك</p>
              )}
            </div>
          </div>
        )}

      </div>

      {/* ── Toast ── */}
      {toast && <Toast notification={toast} onClose={() => setToast(null)} duration={5000} />}

      {/* ── Terms Modal ── */}
      {showTerms && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" dir="rtl" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowTerms(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900">الشروط والأحكام</h2>
              <button type="button" onClick={() => setShowTerms(false)} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 transition border-0">
                <AppIcon name="X" size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-5">
              {termsLoading
                ? <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>
                : termsText
                  ? <div className="text-right" dangerouslySetInnerHTML={{ __html: termsText }} />
                  : <div className="text-center py-12 text-slate-400 text-sm">لم يتم إضافة محتوى الشروط والأحكام بعد</div>}
            </div>
            <div className="flex justify-end px-6 py-4 border-t border-slate-100 bg-slate-50">
              <button type="button" onClick={() => setShowTerms(false)} className="px-6 py-2.5 bg-primary text-white text-sm font-bold rounded-xl hover:opacity-90 transition border-0">
                إغلاق
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default PhotographerRegistrationWizard;
